import styled from '@emotion/styled';
import { Colors } from '../../common/Colors';
import { Variables } from '../../common/Variables';
import { helperTypographyText } from '../../helpers';
import { getRem } from '../../utils';
import { HeadProps, HoverStyleType, TableContainerProps } from './types';

const HEIGHT_CELL = 50;
const GAP = 20;

export const ThInner = styled('div')`
  position: relative;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% - 1px);
    height: 0;
    border-top: 1px solid ${Colors.ut.border10};
  }
`;

export const TableContainer = styled('div')<TableContainerProps>`
  position: relative;
  z-index: 1;
  margin-left: ${props => getRem(-(props.sideRowPadding ?? GAP))};
  margin-right: ${props => getRem(-(props.sideRowPadding ?? GAP))};

  ${ThInner},
  td {
    padding-left: ${props => getRem((props.betweenCellsPadding ?? GAP) / 2)};
    padding-right: ${props => getRem((props.betweenCellsPadding ?? GAP) / 2)};
  }

  td:first-of-type,
  th:first-of-type ${ThInner} {
    padding-left: ${props => getRem(props.sideRowPadding ?? GAP)};
  }

  th:first-of-type ${ThInner}:after {
    margin-left: ${props => getRem(props.sideRowPadding ?? GAP)};
  }

  td:last-of-type,
  th:last-of-type ${ThInner} {
    padding-right: ${props => getRem(props.sideRowPadding ?? GAP)};
  }

  th:last-of-type ${ThInner}:after {
    margin-right: ${props => getRem(props.sideRowPadding ?? GAP)};
  }
`;

export const Table = styled('table')`
  width: 100%;
  font-size: 1.4rem;
  line-height: 1.2;
  border-spacing: 0;
`;

export interface CellProps {
  width?: number;
  textAlign?: 'left' | 'right' | 'center';
}

export const Th = styled('th')<CellProps>`
  ${helperTypographyText({ size: 'S-Compact-Height', view: 'hint' })};
  vertical-align: bottom;
  width: ${props => getRem(props.width)};
  padding: 0;
  text-align: left;
  font-weight: 400;
  text-align: ${props => props.textAlign};
`;

export const Td = styled('td')<CellProps>`
  vertical-align: middle;
  width: ${props => getRem(props.width)};
  text-align: ${props => props.textAlign};
`;

export const Thead = styled('thead')<HeadProps>`
  ${Th},
  ${Td} {
    position: ${props => props.position};
    z-index: 2;
    top: 0;
    padding-bottom: ${props => getRem(props.marginBottom ?? 0)};
  }

  ${ThInner} {
    padding-top: ${props => getRem(props.paddingTop ?? 0)};
    padding-bottom: ${props => getRem(props.paddingBottom ?? 15)};
    background-color: ${props => props.backgroundColor};
  }
`;

export interface TbodyProps {
  rowHeight?: number;
  hoverStyle?: HoverStyleType;
}

export const Tr = styled('tr')`
  &:after {
    content: '';
    position: absolute;
    z-index: -1;
    left: 0;
    right: 0;
    background-image: linear-gradient(90deg, rgba(0, 0, 0, 0.05) 68%, rgba(0, 0, 0, 0) 83%);
    visibility: hidden;
    opacity: 0;
    transition-property: visibility, opacity;
    transition-duration: ${Variables.listTransitionDuration};
    transition-timing-function: ${Variables.listTimingFunction};
  }

  &:hover {
    &:after {
      visibility: visible;
      opacity: 1;
      transition-duration: ${Variables.listTransitionDurationHover};
      transition-timing-function: ${Variables.listTimingFunctionHover};
    }
  }
`;

export const Tbody = styled('tbody')<TbodyProps>`
  td {
    height: ${props => getRem(props.rowHeight ?? HEIGHT_CELL)};
  }

  ${Tr}:after {
    height: ${props => getRem(props.rowHeight ?? HEIGHT_CELL)};
    background: ${({ hoverStyle = 'gradient' }) => hoverStyle === 'normal' && Colors.ut.input};
  }
`;

export const Separator = styled('td')`
  ${helperTypographyText({ size: 'S-Compact-Height', view: 'hint', caps: true })};
  padding: 1.4rem 2rem 1.5rem;

  &:before {
    content: '';
    display: block;
    height: 0;
    margin-bottom: 2.3rem;
    border-top: 1px solid ${Colors.ut.border10};
  }
`;
